// Servicio de Ofertas
// Gestiona las ofertas y lotes de ofertas

import { getCollection, getDocument, createDocument, updateDocument, deleteDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'

const OFFERS_COLLECTION = 'offers'

/**
 * Generar número único de oferta
 * @returns {string} Número de oferta (OF-YYYY-XXX)
 */
export const generateOfferNumber = async () => {
  try {
    const year = new Date().getFullYear()
    const offers = await getCollection(OFFERS_COLLECTION, [
      { field: 'offerNumber', operator: '>=', value: `OF-${year}-000` },
      { field: 'offerNumber', operator: '<=', value: `OF-${year}-999` }
    ])
    
    const numbers = offers
      .map(o => {
        const match = o.offerNumber?.match(/OF-\d{4}-(\d{3})/)
        return match ? parseInt(match[1]) : 0
      })
      .filter(n => n > 0)
      .sort((a, b) => b - a)
    
    const nextNumber = numbers.length > 0 ? numbers[0] + 1 : 1
    return `OF-${year}-${String(nextNumber).padStart(3, '0')}`
  } catch (error) {
    console.error('Error generando número de oferta:', error)
    throw error
  }
}

/**
 * Crear una nueva oferta
 * @param {Object} offerData - Datos de la oferta
 * @returns {Promise<string>} ID de la oferta creada
 */
export const createOffer = async (offerData) => {
  try {
    const offerNumber = await generateOfferNumber()
    
    const data = {
      offerNumber,
      name: offerData.name,
      startDate: offerData.startDate instanceof Timestamp 
        ? offerData.startDate 
        : Timestamp.fromDate(new Date(offerData.startDate)),
      endDate: offerData.endDate instanceof Timestamp 
        ? offerData.endDate 
        : Timestamp.fromDate(new Date(offerData.endDate)),
      active: offerData.active !== false,
      categories: offerData.categories || [],
      products: offerData.products || [],
      confirmed: false,
      confirmedAt: null,
      confirmedBy: null,
      createdAt: Timestamp.now(),
      createdBy: offerData.createdBy || null,
      createdByName: offerData.createdByName || 'Sistema',
      updatedAt: Timestamp.now()
    }
    
    return await createDocument(OFFERS_COLLECTION, data)
  } catch (error) {
    console.error('Error creando oferta:', error)
    throw error
  }
}

/**
 * Obtener una oferta por ID
 * @param {string} offerId - ID de la oferta
 * @returns {Promise<Object>} Oferta
 */
export const getOffer = async (offerId) => {
  try {
    return await getDocument(OFFERS_COLLECTION, offerId)
  } catch (error) {
    console.error('Error obteniendo oferta:', error)
    throw error
  }
}

/**
 * Obtener todas las ofertas
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de ofertas
 */
export const getAllOffers = async (filters = {}) => {
  try {
    const firestoreFilters = []
    
    if (filters.active !== undefined) {
      firestoreFilters.push({ field: 'active', operator: '==', value: filters.active })
    }
    
    if (filters.confirmed !== undefined) {
      firestoreFilters.push({ field: 'confirmed', operator: '==', value: filters.confirmed })
    }
    
    // Obtener todas las ofertas y ordenar en el cliente
    const offers = await getCollection(OFFERS_COLLECTION, firestoreFilters)
    
    // Ordenar por fecha de creación (más recientes primero)
    offers.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
      return bTime - aTime
    })
    
    return offers
  } catch (error) {
    console.error('Error obteniendo ofertas:', error)
    throw error
  }
}

/**
 * Actualizar una oferta
 * @param {string} offerId - ID de la oferta
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<void>}
 */
export const updateOffer = async (offerId, updates) => {
  try {
    const data = {
      ...updates,
      updatedAt: Timestamp.now()
    }
    
    // Convertir fechas si es necesario
    if (data.startDate && !(data.startDate instanceof Timestamp)) {
      data.startDate = Timestamp.fromDate(new Date(data.startDate))
    }
    if (data.endDate && !(data.endDate instanceof Timestamp)) {
      data.endDate = Timestamp.fromDate(new Date(data.endDate))
    }
    
    await updateDocument(OFFERS_COLLECTION, offerId, data)
  } catch (error) {
    console.error('Error actualizando oferta:', error)
    throw error
  }
}

/**
 * Confirmar una oferta e impactar precios
 * @param {string} offerId - ID de la oferta
 * @param {string} userId - ID del usuario que confirma
 * @param {string} userName - Nombre del usuario que confirma
 * @returns {Promise<void>}
 */
export const confirmOffer = async (offerId, userId, userName) => {
  try {
    const offer = await getOffer(offerId)
    if (!offer) {
      throw new Error('Oferta no encontrada')
    }
    
    if (offer.confirmed) {
      throw new Error('La oferta ya está confirmada')
    }
    
    // Actualizar precios especiales de los productos
    const productsToUpdate = offer.products.filter(p => !p.excluded && p.finalPrice > 0)
    
    for (const productOffer of productsToUpdate) {
      try {
        await updateDocument('products', productOffer.productId, {
          specialPrice: productOffer.finalPrice,
          updatedAt: Timestamp.now()
        })
      } catch (error) {
        console.warn(`Error actualizando precio del producto ${productOffer.productId}:`, error)
        // Continuar con los demás productos aunque uno falle
      }
    }
    
    // Marcar oferta como confirmada
    await updateOffer(offerId, {
      confirmed: true,
      confirmedAt: Timestamp.now(),
      confirmedBy: userId,
      confirmedByName: userName,
      active: true
    })
  } catch (error) {
    console.error('Error confirmando oferta:', error)
    throw error
  }
}

/**
 * Desactivar una oferta y limpiar precios especiales
 * @param {string} offerId - ID de la oferta
 * @returns {Promise<void>}
 */
export const deactivateOffer = async (offerId) => {
  try {
    const offer = await getOffer(offerId)
    if (!offer) {
      throw new Error('Oferta no encontrada')
    }
    
    // Limpiar precios especiales de los productos afectados
    const productsToUpdate = offer.products.filter(p => !p.excluded)
    
    for (const productOffer of productsToUpdate) {
      try {
        await updateDocument('products', productOffer.productId, {
          specialPrice: 0,
          updatedAt: Timestamp.now()
        })
      } catch (error) {
        console.warn(`Error limpiando precio especial del producto ${productOffer.productId}:`, error)
      }
    }
    
    // Desactivar oferta
    await updateOffer(offerId, {
      active: false,
      updatedAt: Timestamp.now()
    })
  } catch (error) {
    console.error('Error desactivando oferta:', error)
    throw error
  }
}

/**
 * Eliminar una oferta (solo si no está confirmada)
 * @param {string} offerId - ID de la oferta
 * @returns {Promise<void>}
 */
export const deleteOffer = async (offerId) => {
  try {
    const offer = await getOffer(offerId)
    if (offer && offer.confirmed) {
      throw new Error('No se puede eliminar una oferta confirmada. Desactívala en su lugar.')
    }
    
    await deleteDocument(OFFERS_COLLECTION, offerId)
  } catch (error) {
    console.error('Error eliminando oferta:', error)
    throw error
  }
}

